import React from "react";

/**
 * Shimmer skeleton — replaces blank-then-pop loading states.
 * Uses CSS keyframe (in theme.css) for a continuous sweep.
 *
 * Variants: rect (default), text, circle.
 * Shape layouts: Pipeline, Accounts, Quotes, Invoices, Dashboard, Reports.
 */
export function Shimmer({
  className = "",
  variant = "rect",
  width,
  height,
  testid,
}) {
  const base = "shimmer-skel relative overflow-hidden bg-white/5 dark:bg-white/3";
  const shape =
    variant === "circle" ? "rounded-full" :
    variant === "text"   ? "rounded h-3" :
                           "rounded-lg";
  const style = {};
  if (width)  style.width  = typeof width  === "number" ? `${width}px`  : width;
  if (height) style.height = typeof height === "number" ? `${height}px` : height;
  return <div className={[base, shape, className].join(" ")} style={style} data-testid={testid} />;
}

/** Card-shaped skeleton (matches glass cards) */
export function ShimmerCard({ lines = 2, className = "", testid }) {
  return (
    <div className={["glass p-5 space-y-3", className].join(" ")} data-testid={testid}>
      <div className="flex items-center justify-between">
        <Shimmer className="w-1/3 h-3" />
        <Shimmer variant="circle" className="h-9 w-9" />
      </div>
      <Shimmer className="w-2/3 h-7" />
      {Array.from({ length: lines - 1 }).map((_, i) => (
        <Shimmer key={i} className="w-1/2 h-3" />
      ))}
    </div>
  );
}

/** Row-shaped skeleton (matches table rows / list items) */
export function ShimmerRow({ className = "", testid }) {
  return (
    <div className={["glass p-3 flex items-center gap-3", className].join(" ")} data-testid={testid}>
      <Shimmer variant="circle" className="h-9 w-9" />
      <div className="flex-1 space-y-2">
        <Shimmer className="w-1/2 h-3" />
        <Shimmer className="w-1/3 h-2.5" />
      </div>
      <Shimmer className="w-16 h-6" />
    </div>
  );
}

/** Full dashboard skeleton layout */
export function ShimmerDashboard() {
  return (
    <div data-testid="shimmer-dashboard" className="space-y-6">
      <Shimmer className="h-28 w-full" />
      <Shimmer className="h-10 w-2/3" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {Array.from({ length: 4 }).map((_, i) => <ShimmerCard key={i} />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Shimmer className="h-[280px] lg:col-span-1" />
        <Shimmer className="h-[280px] lg:col-span-2" />
      </div>
    </div>
  );
}

export default Shimmer;
